import dayjs from "dayjs";
import objectSupport from "dayjs/plugin/objectSupport";
import { APIEndpoints } from "./APIEndpoints";
dayjs.extend(objectSupport);

export function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

export function checkFutureDate(day, month, year) {
  const valueDate = dayjs({
    year: year,
    month: month - 1,
    day: day,
  });
  const isPast = valueDate.isBefore(dayjs());

  return !isPast;
}

export function checkTwoDaysFromNow(day, month, year) {
  const valueDate = dayjs({
    year: year,
    month: month - 1,
    day: day,
  });
  return valueDate.startOf("day").diff(dayjs().startOf("day"), "day") >= 2;
}

export function checkTwoYearsFromNow(day, month, year) {
  const valueDate = dayjs({
    year: year,
    month: month - 1,
    day: day,
  });
  return valueDate.isBefore(
    dayjs().startOf("day").add(2, "year").add(1, "day"),
    "day"
  );
}

export function checkAgeDifference(
  date1,
  date1Format,
  date2,
  date2Format,
  difference
) {
  const one = dayjs(date1, date1Format);
  const two = dayjs(date2, date2Format);

  const diffYears = two.diff(one, "year");
  return diffYears > difference;
}

export function formatUSPhone(number) {
  number = number.replace(/\D/g, "");
  if (number.length > 6) {
    return `(${number.substring(0, 3)}) ${number.substring(
      3,
      6
    )}-${number.substring(6)}`;
  } else if (number.length > 3) {
    return `(${number.substring(0, 3)}) ${number.substring(3, 6)}`;
  } else {
    return number;
  }
}

export function formatMaskedUSPhone(number) {
  if (number.length > 6) {
    return `(${number.substring(0, 3)}) ${number.substring(
      3,
      6
    )}-${number.substring(6)}`;
  } else if (number.length > 3) {
    return `(${number.substring(0, 3)}) ${number.substring(3, 6)}`;
  } else {
    return number;
  }
}

export function amountFormatter(value) {
  if (value === null) {
    return "N/A";
  }
  return parseFloat(value) > 0
    ? "$" +
        Number(value).toLocaleString("en-US", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
    : "$0.00";
}

export function percentageFormatter(value) {
  return parseFloat(value) > 0
    ? parseFloat(value * 100).toFixed(2) + "%"
    : "0.00%";
}

export function percentageFormatterForDisplay(value) {
  return parseFloat(value) > 0 ? parseFloat(value).toFixed(2) + "%" : "0.00%";
}

export function openPDF(blob) {
  const fileURL = window.URL.createObjectURL(
    new Blob([blob], { type: "application/pdf" })
  );
  window.open(fileURL, "_blank");
}

export function downloadPDF(blob, fileName) {
  const fileURL = window.URL.createObjectURL(new Blob([blob]));
  const fileLink = document.createElement("a");
  fileLink.href = fileURL;

  fileLink.setAttribute("download", fileName + ".pdf");
  document.body.appendChild(fileLink);
  fileLink.click();
}

export function getRequiredIds(participantDetails, taxTypeCode, contractId) {
  const obj = {
    productSponsorId: participantDetails?.productSponsorId,
    productId: participantDetails?.productId,
    ownershipId: participantDetails?.ownershipId,
    planId: participantDetails?.extPlanId,
    participantId: participantDetails?.participantId,
  };
  if (taxTypeCode) obj.taxTypeCode = taxTypeCode;
  if (contractId) obj.contractId = contractId;
  return obj;
}

export function getGender(genderCode) {
  const obj = {
    M: "Male",
    F: "Female",
    O: "Other",
  };

  return obj[genderCode];
}

export function getMaritalStatus(maritalCode) {
  const obj = {
    S: "Single",
    M: "Married",
    O: "Other",
    D: "Divorced",
    W: "Widowed",
    U: "-",
  };

  return obj[maritalCode];
}

export function getEmploymentStatus(employmentCode) {
  const obj = {
    A: "Active",
    R: "Retired",
    T: "Terminated",
    D: "Deceased",
  };

  return obj[employmentCode];
}

function replaceNumbers(value) {
  return value.replace(/[^\d[a-z]]/g, "").replace(/\d/g, "X");
}

export function maskSSN(ssn) {
  if (ssn) {
    let val =
      replaceNumbers(ssn.substring(0, 3)) +
      "-" +
      replaceNumbers(ssn.substring(3, 5)) +
      "-" +
      ssn.substr(5);

    return val;
  }
}

export function getStateName(state, data) {
  if (data?.usStateList) {
    const obj = data?.usStateList?.find((e) => e.shortCode === state);
    return obj?.governmentUnitName;
  }
}

export function getStateId(state, data) {
  if (data?.usStateList) {
    const obj = data?.usStateList?.find((e) => e.shortCode === state);
    return obj?.governmentUnitId;
  }
}

export function getCountryName(country, data) {
  if (data?.countryList) {
    const obj = data?.countryList?.find((e) => e.shortCode === country);
    return obj?.governmentUnitName || "US";
  }
}

export function getCountryId(country, data) {
  if (data?.countryList) {
    const obj = data?.countryList?.find((e) => e.shortCode === country);
    return obj?.governmentUnitId;
  }
}

export function getMonthName(month) {
  month = month.replace("0", "");
  return [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ][month];
}

export function getTaxTypeLabel(type) {
  const obj = {
    R: "roth",
    Y: "post-tax",
    N: "pre-tax",
  };
  return obj[type];
}

export async function convertBlobToFile(blobURL, fileName, fileType) {
  try {
    const response = await fetch(blobURL);
    const blob = await response.arrayBuffer();
    const file = new File([blob], fileName, {
      type: fileType,
    });
    return file;
  } catch (err) {
    return "";
  }
}

export function getYears() {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 3 }, (_, i) => currentYear + i);
}

export function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

export function getCurrentDay() {
  return dayjs().format("MM/DD/YYYY");
}

export function parseHtmlToPDFText(htmlString, Text, Link) {
  const temp = document.createElement("div");
  temp.innerHTML = htmlString;

  const children = Array.from(temp.childNodes);
  return children.map((node, index) => {
    if (node.nodeType === 3) {
      return <Text key={index}>{node.textContent}</Text>;
    } else if (node.nodeName === "A") {
      return (
        <Link
          key={index}
          src={node.getAttribute("href")}
          style={{ color: "blue", textDecoration: "underline" }}
        >
          {node.textContent}
        </Link>
      );
    } else {
      return null;
    }
  });
}

export function getAge(dateString) {
  const birthDate = dayjs(dateString, "MM/DD/YYYY");
  const today = dayjs();
  return today.diff(birthDate, "year");
}

export function formatDOB(dateString) {
  return dayjs(dateString).format("MM/DD/YYYY");
}

export function getCityStateZip(participantDetails) {
  let string = participantDetails?.city;
  if (participantDetails?.usResident === "Y") {
    string += ", " + participantDetails?.pptStateCountryValue;
  }
  string += ", " + participantDetails.zipCode;
  return string;
}

export function getAnnuitantCityStateZip(data) {
  let string = data?.annuitantCity;
  const state = data?.annuitantStateName || data?.annuitantState;
  if (data?.usResident === "Y") {
    string += ", " + state;
  }
  string += ", " + data.annuitantZipCode;
  return string;
}

export function getPDFHeader(View, Image, styles, Text, participantDetails) {
  return (
    <View style={styles.header}>
      <Image
        src={APIEndpoints.images(localStorage.getItem("tenant"), "logo.png")}
        style={styles.logo}
      />
      <View style={styles.userInfo}>
        <Text>
          {participantDetails?.firstName} {participantDetails?.middleName}
          &nbsp;
          {participantDetails?.lastName}
        </Text>
        <Text>{participantDetails?.planName}</Text>
      </View>
    </View>
  );
}

export function getPDFFooter(View, styles, Text) {
  return (
    <View style={styles.footer}>
      <Text>{new Date().toLocaleDateString()}</Text>
      <Text>
        {new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })}
      </Text>
    </View>
  );
}

export function getPDFStylesheet(StyleSheet) {
  return StyleSheet.create({
    page: {
      fontSize: 10,
      padding: 40,
      lineHeight: 1.6,
      color: "#353535",
    },
    sectionTitle: {
      fontWeight: "bold",
      fontSize: 13,
      marginBottom: 8,
      borderBottomWidth: 1,
      borderBottomColor: "#b2b0b0",
      borderBottomStyle: "solid",
      paddingBottom: 4,
      marginTop: 16,
    },
    row: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 6,
    },
    label: {
      fontWeight: "bold",
      marginBottom: 3,
    },
    paragraph: {
      marginBottom: 6,
    },
    header: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-end",
      marginBottom: 40,
    },
    logo: {
      width: 150,
      height: 60,
    },
    userInfo: {
      display: "flex",
      alignItems: "flex-end",
      textAlign: "right",
      fontSize: 10,
      marginBottom: 0,
    },
    footer: {
      marginTop: 20,
    },
  });
}
